import getSymbolFromCurrency from 'currency-symbol-map'
import cc from 'currency-codes'
import { NumberField, NumberFieldProps } from 'hic-components'
import { useCallback, useMemo } from 'react'
import { Currency } from 'src/api/currency'
import { InputAdornment } from '@mui/material'

export type HicAmountFieldProps = Omit<NumberFieldProps, 'decimals'> & {
  currency: Currency
  hideDecimals?: boolean
}

export default function HicAmountField(props: HicAmountFieldProps) {
  const { currency, value: pValue, onChange: pOnChange, InputProps, hideDecimals, ...otherProps } = props
  const { symbol, decimals } = useMemo(() => {
    const symbol = getSymbolFromCurrency(currency)
    const decimals = cc.code(currency)?.digits || 2
    return { symbol, decimals }
  }, [currency])

  const value = useMemo(() => {
    if (pValue === undefined) {
      return undefined
    }
    return hicAmountToNumber({ hicAmount: pValue, decimals })
  }, [pValue, decimals])

  const onChange = useCallback(
    (value: number | null) => {
      pOnChange?.(numberToHicAmount({ number: value, decimals }))
    },
    [pOnChange, decimals],
  )

  return (
    <NumberField //
      {...otherProps}
      value={value}
      onChange={onChange}
      decimals={hideDecimals ? 0 : decimals}
      InputProps={{ ...InputProps, endAdornment: InputProps?.endAdornment ?? <InputAdornment position="end">{symbol}</InputAdornment> }}
      inputProps={{ style: { textAlign: 'right' } }}
    />
  )
}

type HicAmountToNumber =
  | {
      hicAmount: number | null
      decimals: number
    }
  | {
      hicAmount: number | null
      currency: Currency
    }

export function hicAmountToNumber(props: HicAmountToNumber): number | null {
  const hicAmount = props.hicAmount
  const decimals = 'decimals' in props ? props.decimals : cc.code(props.currency)?.digits || 2

  if (hicAmount === null || Number.isNaN(hicAmount)) {
    return null
  }
  const power = 10 ** Math.floor(decimals)
  const number = Math.floor(hicAmount) / power
  return number
}

type NumberToHicAmount =
  | {
      number: number | null
      decimals: number
    }
  | {
      number: number | null
      currency: Currency
    }

export function numberToHicAmount(props: NumberToHicAmount): number | null {
  const number = props.number
  const decimals = 'decimals' in props ? props.decimals : cc.code(props.currency)?.digits || 2
  if (number === null || Number.isNaN(number)) {
    return null
  } else {
    const power = 10 ** Math.floor(decimals)
    const poweredAmount = number * power
    const integerAmount = Math.round(poweredAmount)
    return integerAmount
  }
}
